import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { API_BASE_URL } from "../../config";

// Simulating async login/signup
export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (credentials, thunkAPI) => {
    try {
      const response = await fetch(`${API_BASE_URL}/login/customer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        const errorData = await response.json();
        return thunkAPI.rejectWithValue(errorData.message); // Use the error message from the server response
      }

      return await response.json();
    } catch (error) {
      return thunkAPI.rejectWithValue(
        "Something went wrong. Please try again later."
      );
    }
  }
);

export const signupUser = createAsyncThunk(
  "auth/signupUser",
  async (credentials, thunkAPI) => {
    try {
      const response = await fetch(`${API_BASE_URL}/user`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        const errorData = await response.json();
        return thunkAPI.rejectWithValue(errorData.message); // Use the error message from the server response
      }

      return await response.json();
    } catch (error) {
      return thunkAPI.rejectWithValue(
        "Something went wrong. Please try again later."
      );
    }
  }
);
export const updateUser = createAsyncThunk(
  "auth/updateUser",
  async (payload, thunkAPI) => {
    try {
      const response = await fetch(`${API_BASE_URL}/user`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${payload.token}`,
        },
        body: JSON.stringify(payload.user),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.log("errorData", errorData);
        return thunkAPI.rejectWithValue(errorData.message);
      }

      return await response.json();
    } catch (error) {
      return thunkAPI.rejectWithValue(
        "Something went wrong. Please try again later."
      );
    }
  }
);
export const sendResetPasswordEmail = createAsyncThunk(
  "auth/sendResetPasswordEmail",
  async (email, thunkAPI) => {
    try {
      const response = await fetch(`${API_BASE_URL}/user/forget`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(email),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.log("errorData", errorData);
        return thunkAPI.rejectWithValue(errorData.message);
      }

      return await response.json();
    } catch (error) {
      return thunkAPI.rejectWithValue(
        "Something went wrong. Please try again later."
      );
    }
  }
);
export const verifyOTP = createAsyncThunk(
  "auth/verifyOTP",
  async ({ email, otp }, thunkAPI) => {
    try {
      const response = await fetch(`${API_BASE_URL}/user/verify-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, otp }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.log("errorData", errorData);
        return thunkAPI.rejectWithValue(errorData.message);
      }

      return await response.json();
    } catch (error) {
      return thunkAPI.rejectWithValue(
        "Something went wrong. Please try again later."
      );
    }
  }
);
export const updatePassword = createAsyncThunk(
  "auth/updatePassword",
  async (payload, thunkAPI) => {
    try {
      const response = await fetch(`${API_BASE_URL}/user/update-password`, {
        // Update endpoint if needed
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Error:", errorData);
        return thunkAPI.rejectWithValue(errorData.message);
      }

      return await response.json();
    } catch (error) {
      return thunkAPI.rejectWithValue(
        "Something went wrong. Please try again later."
      );
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : null,
    token: localStorage.getItem("token") ? localStorage.getItem("token") : null,
    loading: false,
    loginLoading: false,
    signupLoading: false,
    error: null,
    errorText: "", // Store dynamic error messages here
  },
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      localStorage.removeItem("token");
      localStorage.removeItem("user");
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loginLoading = true;
        state.error = null;
        state.errorText = ""; // Reset error text
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loginLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.accessToken;
        localStorage.setItem("token", action.payload.accessToken);
        localStorage.setItem("user", JSON.stringify(action.payload.user));
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loginLoading = false;
        state.errorText = action.payload; // Save dynamic error message
      })
      .addCase(signupUser.pending, (state) => {
        state.signupLoading = true;
        state.error = null;
        state.errorText = ""; // Reset error text
      })
      .addCase(signupUser.fulfilled, (state, action) => {
        state.signupLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.accessToken;
        localStorage.setItem("token", action.payload.accessToken);
        localStorage.setItem("user", JSON.stringify(action.payload.user));
      })
      .addCase(signupUser.rejected, (state, action) => {
        state.signupLoading = false;
        state.errorText = action.payload; // Save dynamic error message
      })
      .addCase(updateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.errorText = "";
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        console.log("Action", action);
        state.loading = false;
        const { firstName, lastName, displayName, email, phoneNumber } =
          action.payload.data;
        const data = {
          firstName,
          lastName,
          displayName,
          email,
          phone: phoneNumber,
        };
        state.user = data;
        localStorage.setItem("user", JSON.stringify(data)); // Update user in localStorage
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false;
        state.errorText = action.payload;
      })
      .addCase(sendResetPasswordEmail.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(sendResetPasswordEmail.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
      })
      .addCase(sendResetPasswordEmail.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.success = false;
      })
      // verifyOTP cases...
      .addCase(verifyOTP.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.isOTPSuccessful = false;
      })
      .addCase(verifyOTP.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isOTPSuccessful = true;
        // Handle any additional data, like updating user state if needed
      })
      .addCase(verifyOTP.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.isOTPSuccessful = false;
      })
      .addCase(updatePassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updatePassword.fulfilled, (state) => {
        state.isLoading = false;
        state.success = true;
      })
      .addCase(updatePassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.success = false;
      });
  },
});

export const { logout } = authSlice.actions;

export default authSlice.reducer;
